import { Property } from '@stitches/react/types/css';
import { FC, useContext, useMemo } from 'react';
import { BASKETBALL_STANDINGS } from '../../../../constants/widgetIds';
import { INormalizedKnockoutMatch } from '../../../../types';
import { ConfigContext, SMPFlex, SMPText, StyledSMPTextVariants } from '../../../index';
import { FINAL } from '../../../../constants/playoff';
import { SMPCupIcon } from '../../atoms/icons/SMPCupIcon';
import { Sports, validForCustomSportsStyles } from '../../../../constants/sportType';

const getTextColor = (sport: string | undefined, showAggEvents: boolean | undefined, customColor?: string) => {
  if (customColor) {
    return customColor;
  }

  if (validForCustomSportsStyles.includes(sport || '')) {
    return showAggEvents ? 'var(--colors-gray0)' : 'var(--colors-hiContrast)';
  }

  return 'var(--colors-hiContrast)';
};

type IGameScoreProps = {
  homeTeamScore?: string | number | null;
  awayTeamScore?: string | number | null;
  matchUrlTarget?: string;
  matchLink?: string;
  verticalAlign?: boolean;
  variant?: StyledSMPTextVariants['variant'];
  fontWeight?: '400' | '700' | '900';
  homeTeamWeight?: StyledSMPTextVariants['weight'];
  awayTeamWeight?: StyledSMPTextVariants['weight'];
  handleEventClick?: (id: string | number) => void;
  eventId?: string;
  isMobile?: boolean;
  width?: string;
  fontSize?: Property.FontSize;
  delimiter?: string;
  header?: string;
  penaltyShootoutScore?: INormalizedKnockoutMatch['penaltyShootoutScore'];
  groupName?: string;
  sport?: string;
  showAggEvents?: boolean;
  color?: string;
};

export const GameScore = ({
  homeTeamScore,
  awayTeamScore,
  matchLink,
  matchUrlTarget,
  verticalAlign,
  fontWeight,
  variant = 'contrast',
  homeTeamWeight,
  awayTeamWeight,
  handleEventClick,
  eventId,
  width,
  fontSize,
  delimiter = ':',
  header,
  penaltyShootoutScore,
  groupName,
  sport,
  showAggEvents,
  color,
}: IGameScoreProps): JSX.Element => {
  const { dataWidgetId, dataWidgetSport } = useContext(ConfigContext);
  const actualHomeTeamWeight = homeTeamWeight ?? fontWeight ?? (Number(homeTeamScore) > Number(awayTeamScore) ? '700' : '400');
  const actualAwayTeamWeight = awayTeamWeight ?? fontWeight ?? (Number(awayTeamScore) > Number(homeTeamScore) ? '700' : '400');
  const style: React.CSSProperties = { width: 'auto', minWidth: undefined, maxWidth: undefined };
  const textColorStyle = getTextColor(sport, showAggEvents, color);
  const textStyles: React.CSSProperties =
    variant === 'accentText' ? { fontSize, position: 'relative' } : { fontSize, position: 'relative', color: textColorStyle };

  if (width) {
    style.width = undefined;
    style.minWidth = width;
    style.maxWidth = parseInt(width) * 2 + 'px';
  }

  const scoresWithPenaltyIndicator = useMemo(() => {
    if (typeof penaltyShootoutScore?.home === 'number' && typeof penaltyShootoutScore.away === 'number') {
      return {
        home: (
          <>
            {homeTeamScore}
            {penaltyShootoutScore.home > penaltyShootoutScore.away && <Asterisk />}
          </>
        ), // If home team won the penalty shootout, add an asterisk to the score
        away: (
          <>
            {awayTeamScore}
            {penaltyShootoutScore.away > penaltyShootoutScore.home && <Asterisk />}
          </>
        ), // If away team won the penalty shootout, add an asterisk to the score
      };
    }

    return { home: homeTeamScore, away: awayTeamScore };
  }, [awayTeamScore, homeTeamScore, penaltyShootoutScore?.away, penaltyShootoutScore?.home]);

  const isPale = useMemo(() => {
    if (dataWidgetSport === Sports.BASKETBALL && dataWidgetId !== BASKETBALL_STANDINGS) {
      return {
        home: Number(actualHomeTeamWeight) < 700,
        away: Number(actualAwayTeamWeight) < 700,
      };
    }

    // football
    return {
      home: Number(awayTeamScore) > Number(homeTeamScore),
      away: Number(homeTeamScore) > Number(awayTeamScore),
    };
  }, [dataWidgetSport, dataWidgetId, awayTeamScore, homeTeamScore, actualHomeTeamWeight, actualAwayTeamWeight]);

  return (
    <>
      {verticalAlign ? (
        <a
          href={matchLink}
          target={matchUrlTarget}
          onClick={() => handleEventClick && eventId && handleEventClick(eventId)}
          style={{ display: 'inline-flex', textDecoration: 'none' }}
        >
          <SMPFlex variant='column' align='center' justify='spaceBetween' style={style}>
            {header !== undefined && (
              <SMPFlex css={{ width: '100%', height: '1em', fontSize: '$2_5' }}>
                <SMPText size='2_5' textAlign='center' weight='400' variant='scoreHeader' pale>
                  {header}
                </SMPText>
              </SMPFlex>
            )}

            <SMPFlex align='center' css={{ height: '$5_5' }}>
              <SMPText variant={variant} size='3' weight={actualHomeTeamWeight} style={textStyles} pale={isPale.home}>
                {scoresWithPenaltyIndicator.home ?? '-'}
              </SMPText>
            </SMPFlex>
            {Number(homeTeamScore) > Number(awayTeamScore) && groupName === FINAL && sport === Sports.ICE_HOCKEY ? (
              <SMPFlex
                style={{
                  position: 'absolute',
                  left: '100%',
                  top: '28%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <SMPCupIcon width='5' height='5' />
              </SMPFlex>
            ) : null}

            <SMPFlex align='center' css={{ height: '$5_5' }}>
              <SMPText variant={variant} size='3' weight={actualAwayTeamWeight} style={textStyles} pale={isPale.away}>
                {scoresWithPenaltyIndicator.away ?? '-'}
              </SMPText>
            </SMPFlex>
            {Number(awayTeamScore) > Number(homeTeamScore) && groupName === FINAL && sport === Sports.ICE_HOCKEY ? (
              <SMPFlex
                style={{
                  position: 'absolute',
                  left: '100%',
                  top: '71%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <SMPCupIcon width='5' height='5' />
              </SMPFlex>
            ) : null}
          </SMPFlex>
        </a>
      ) : (
        <a
          href={matchLink}
          target={matchUrlTarget}
          style={{ display: 'inline-flex', textDecoration: 'none' }}
          onClick={() => handleEventClick && eventId && handleEventClick(eventId)}
        >
          <SMPFlex align='center' justify='spaceAround'>
            <SMPText variant='contrast' size={fontSize ? 3 : 6} weight={fontWeight ?? '900'}>
              {homeTeamScore} {delimiter} {awayTeamScore}
            </SMPText>
          </SMPFlex>
        </a>
      )}
    </>
  );
};

const Asterisk: FC = () => {
  return <span style={{ position: 'absolute', left: '8px' }}>*</span>;
};
